<script setup>
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";

const props = defineProps({
    data: {
        type: Object,
        required: true,
    },
    // Si true, émet des événements au lieu de naviguer via Inertia
    useEvents: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["page-change"]);

const filteredLinks = computed(() => {
    // Vérification SSR : s'assurer que links existe et est un tableau
    if (!props.data?.links || !Array.isArray(props.data.links)) {
        return [];
    }

    const links = props.data.links.filter(
        (link) =>
            !link.label.includes("Previous") && !link.label.includes("Next"),
    );

    // Trouver l'index de la page courante
    const currentIndex = links.findIndex((link) => link.active);

    // Garder seulement 2 pages avant et après la page courante
    return links.filter((link, index) => {
        const distance = Math.abs(index - currentIndex);
        return distance <= 1 || index === 0 || index === links.length - 1;
    });
});

const handlePageClick = (url, event) => {
    if (props.useEvents && url) {
        event.preventDefault();
        emit("page-change", url);
    }
};
</script>

<template>
    <ul
        v-if="data?.last_page && data.last_page !== 1"
        class="ais-Pagination-list"
    >
        <li>
            <component
                :is="useEvents ? 'a' : Link"
                class="ais-Pagination-link"
                v-bind="
                    useEvents
                        ? {}
                        : {
                              href: data.prev_page_url || '#',
                              preserveState: true,
                          }
                "
                @click="handlePageClick(data.prev_page_url, $event)"
            >
                <span
                    class="ais-Pagination-item previousPage"
                    :class="{ disabled: !data.prev_page_url }"
                    >‹</span
                >
            </component>
        </li>

        <li v-for="(link, index) in filteredLinks" :key="index">
            <component
                :is="useEvents ? 'a' : Link"
                class="ais-Pagination-link"
                :class="{ disabled: link.label === '...' }"
                v-bind="
                    useEvents ? {} : { href: link.url, preserveState: true }
                "
                @click="handlePageClick(link.url, $event)"
            >
                <span
                    class="ais-Pagination-item"
                    :class="{ selected: link.active }"
                    >{{ link.label }}</span
                >
            </component>
        </li>

        <li>
            <component
                :is="useEvents ? 'a' : Link"
                class="ais-Pagination-link"
                v-bind="
                    useEvents
                        ? {}
                        : {
                              href: data.next_page_url || '#',
                              preserveState: true,
                          }
                "
                @click="handlePageClick(data.next_page_url, $event)"
            >
                <span
                    class="ais-Pagination-item nextPage"
                    :class="{ disabled: !data.next_page_url }"
                    >›</span
                >
            </component>
        </li>
    </ul>
</template>

<style lang="scss">
.ais-Pagination-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.ais-Pagination-link {
    cursor: pointer;
}

.ais-Pagination-item {
    padding: 0.5rem 0.75rem;
}

@media (max-width: 640px) {
    .ais-Pagination-list {
        gap: 0.15rem;
    }

    .ais-Pagination-item {
        padding: 0.2rem 0.02rem;
        font-size: 0.875rem;
    }
}
</style>
